import React, { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import {
    createSupportTicket,
    SupportTicketSeverity,
    SupportTicketSummary,
} from "../services/supportTicketsService";
import SupportTicketSelect from "./SupportTicketSelect";
import "../styles/feature-requests.css";
import "../styles/support-tickets.css";

const TITLE_MAX = 500;
const DESCRIPTION_MAX = 2000;

const SEVERITY_OPTIONS = [
    { value: "HIGH", label: "High" },
    { value: "MEDIUM", label: "Medium" },
    { value: "LOW", label: "Low" },
];

type Props = {
    open: boolean;
    onClose: () => void;
    onSubmitted: (ticket: SupportTicketSummary) => void;
};

export default function SubmitSupportTicketModal({ open, onClose, onSubmitted }: Props) {
    const titleId = useId();
    const descriptionId = useId();
    const severityId = useId();
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [severity, setSeverity] = useState<SupportTicketSeverity | "">("");
    const [titleError, setTitleError] = useState<string | null>(null);
    const [descriptionError, setDescriptionError] = useState<string | null>(null);
    const [severityError, setSeverityError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [touched, setTouched] = useState({ title: false, description: false });

    useEffect(() => {
        if (!open) return;
        setTitle("");
        setDescription("");
        setSeverity("");
        setTitleError(null);
        setDescriptionError(null);
        setSeverityError(null);
        setSubmitError(null);
        setSubmitting(false);
        setTouched({ title: false, description: false });
    }, [open]);

    const validateTitle = (value: string): string | null => {
        const trimmed = value.trim();
        if (!trimmed) return "Please enter a title.";
        if (trimmed.length > TITLE_MAX) return "Title must be 500 characters or fewer.";
        return null;
    };

    const validateDescription = (value: string): string | null => {
        const trimmed = value.trim();
        if (!trimmed) return "Please enter a description.";
        if (trimmed.length > DESCRIPTION_MAX) return "Description must be 2,000 characters or fewer.";
        return null;
    };

    const validateSeverity = (value: SupportTicketSeverity | ""): string | null => {
        if (!value) return "Please select a severity.";
        return null;
    };

    const titleErr = validateTitle(title);
    const descriptionErr = validateDescription(description);
    const severityErr = validateSeverity(severity);
    const isValid = !titleErr && !descriptionErr && !severityErr;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched({ title: true, description: true });
        setTitleError(validateTitle(title));
        setDescriptionError(validateDescription(description));
        setSeverityError(validateSeverity(severity));
        if (!isValid || !severity) return;

        setSubmitting(true);
        setSubmitError(null);
        try {
            const created = await createSupportTicket(title.trim(), description.trim(), severity);
            onSubmitted(created);
            onClose();
        } catch (err: unknown) {
            setSubmitError(err instanceof Error ? err.message : "Failed to submit support ticket.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <div className="feature-idea-modal-backdrop">
            <div
                className="feature-idea-modal support-ticket-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${titleId}-heading`}
                data-testid="submit-support-ticket-modal"
            >
                <div className="feature-idea-modal-header">
                    <button
                        type="button"
                        className="feature-idea-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ×
                    </button>
                    <h2 id={`${titleId}-heading`}>Raise a support ticket</h2>
                    <p>
                        Tell us what you need help with. Our Polaris support team will review your
                        ticket and follow up as soon as possible.
                    </p>
                </div>

                <form className="feature-idea-modal-body" onSubmit={handleSubmit} noValidate>
                    <div className="feature-idea-field">
                        <label htmlFor={titleId}>Title</label>
                        <input
                            id={titleId}
                            type="text"
                            value={title}
                            maxLength={TITLE_MAX}
                            data-testid="support-ticket-title-input"
                            onChange={(e) => {
                                setTitle(e.target.value);
                                if (touched.title) setTitleError(validateTitle(e.target.value));
                            }}
                            onBlur={() => {
                                setTouched((t) => ({ ...t, title: true }));
                                setTitleError(validateTitle(title));
                            }}
                        />
                        <div className="feature-idea-field-meta">
                            {touched.title && titleError ? (
                                <span className="feature-idea-field-error" role="alert">
                                    {titleError}
                                </span>
                            ) : (
                                <span />
                            )}
                            <span>
                                {title.length}/{TITLE_MAX}
                            </span>
                        </div>
                    </div>

                    <div className="feature-idea-field">
                        <label htmlFor={descriptionId}>Description</label>
                        <textarea
                            id={descriptionId}
                            value={description}
                            maxLength={DESCRIPTION_MAX}
                            data-testid="support-ticket-description-input"
                            onChange={(e) => {
                                setDescription(e.target.value);
                                if (touched.description) {
                                    setDescriptionError(validateDescription(e.target.value));
                                }
                            }}
                            onBlur={() => {
                                setTouched((t) => ({ ...t, description: true }));
                                setDescriptionError(validateDescription(description));
                            }}
                        />
                        <div className="feature-idea-field-meta">
                            {touched.description && descriptionError ? (
                                <span className="feature-idea-field-error" role="alert">
                                    {descriptionError}
                                </span>
                            ) : (
                                <span />
                            )}
                            <span>
                                {description.length}/{DESCRIPTION_MAX}
                            </span>
                        </div>
                    </div>

                    <div className="feature-idea-field">
                        <label htmlFor={severityId}>Severity</label>
                        <SupportTicketSelect
                            id={severityId}
                            value={severity}
                            options={SEVERITY_OPTIONS}
                            placeholder="Select severity"
                            data-testid="support-ticket-severity-select"
                            onChange={(next) => {
                                const value = next as SupportTicketSeverity;
                                setSeverity(value);
                                setSeverityError(null);
                            }}
                        />
                        <div className="feature-idea-field-meta">
                            {severityError ? (
                                <span className="feature-idea-field-error" role="alert">
                                    {severityError}
                                </span>
                            ) : (
                                <span />
                            )}
                        </div>
                    </div>

                    {submitError && (
                        <div
                            className="feature-idea-field-error"
                            role="alert"
                            style={{ marginBottom: "0.75rem" }}
                        >
                            {submitError}
                        </div>
                    )}

                    <div className="feature-idea-modal-actions support-ticket-detail-actions">
                        <button
                            type="button"
                            className="support-ticket-close-btn"
                            disabled={submitting}
                            data-testid="support-ticket-cancel-button"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`feature-idea-modal-submit${isValid ? " is-valid" : ""}`}
                            disabled={submitting}
                            data-testid="support-ticket-submit-button"
                        >
                            {submitting ? "Submitting…" : "Submit ticket"}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body,
    );
}
